import React, { useState } from "react";
import { Alert, Button } from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import { useUser } from "../../context/UserContext";
import { useMenu } from "../../context/MenuContext";
import { useToaster } from "../../hooks/useToaster";
import { ModuleNames, SEVERITIES } from "../../data/constants";
import { discardWorkflowTestRun } from "../../services/workflowService";
import {
  DISCARD_TEST_RUN_MESSAGE,
  DISCARD_TEST_RUN_TITLE,
  TEST_RUN_DISCARDED,
  TEST_RUN_NOT_DISCARDED,
  TEST_RUN_NOTICE,
} from "./workflowTestRun";

interface WorkflowTestRunBarProps {
  instanceId: number;
  /** Who started the run - they may always discard it. */
  startedBy: number | null;
  /** Only an open run can be discarded. */
  open: boolean;
  /** Reloads the case once it has been discarded. */
  onDiscarded: () => unknown;
}

/**
 * Epic 80431 (Pooja Pujara, 2026-10-19): marks a Builder test run on the Run screen and offers
 * "Discard test run" to its starter and to a Builder editor. The server checks the same rule and
 * refuses a real case outright; this only decides whether to show the button.
 */
const WorkflowTestRunBar: React.FC<WorkflowTestRunBarProps> = ({ instanceId, startedBy, open, onDiscarded }) => {
  const { userId } = useUser();
  const { permissions } = useMenu();
  const showToaster = useToaster();
  const [confirming, setConfirming] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  const isBuilderEditor = Boolean(
    permissions?.find((permission) => permission.moduleName === ModuleNames.WorkflowBuilder)?.canEdit,
  );
  const canDiscard = open && (isBuilderEditor || (userId !== null && startedBy === userId));

  const handleConfirm = async (confirmed: boolean) => {
    setConfirming(false);
    if (!confirmed) return;
    setDiscarding(true);
    try {
      await discardWorkflowTestRun(instanceId);
      showToaster(TEST_RUN_DISCARDED, SEVERITIES.SUCCESS);
      await onDiscarded();
    } catch (error) {
      // 409: the run finished in the meantime, so there is nothing to discard - show it as it is
      // now rather than an error (PR 33163). 403 / 422 keep the message.
      if ((error as { response?: { status?: number } } | null)?.response?.status === 409) await onDiscarded();
      else showToaster(TEST_RUN_NOT_DISCARDED, SEVERITIES.ERROR);
    } finally {
      setDiscarding(false);
    }
  };

  return (
    <>
      <Alert
        severity="info"
        className="workflow-run__alert workflow-run__test-run"
        action={
          canDiscard ? (
            <Button
              color="inherit"
              size="small"
              startIcon={<DeleteOutline />}
              onClick={() => setConfirming(true)}
              disabled={discarding}
            >
              {DISCARD_TEST_RUN_TITLE}
            </Button>
          ) : undefined
        }
      >
        {TEST_RUN_NOTICE}
      </Alert>
      <ConfirmationDialog
        open={confirming}
        title={DISCARD_TEST_RUN_TITLE}
        message={DISCARD_TEST_RUN_MESSAGE}
        onClose={handleConfirm}
        // Discarding is final, so Enter on open must not be the destructive answer.
        initialFocus="no"
      />
    </>
  );
};

export default WorkflowTestRunBar;
